import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import { ShieldCheck, AlertTriangle, Plus, Loader2, Flag, Bell, Search, Mail, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fuzzyMatch } from "../lib/fuzzySearch";
import { ComplianceScoreRing } from "../components/compliance/ComplianceScoreRing";
import { ObligationRow } from "../components/compliance/ObligationRow";
import { MilestoneRow } from "../components/compliance/MilestoneRow";
import { AiExtractionPanel } from "../components/compliance/AiExtractionPanel";

export default function CompliancePage() {
  const [obligations, setObligations] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showObDialog, setShowObDialog] = useState(false);
  const [showMsDialog, setShowMsDialog] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterContract, setFilterContract] = useState("");
  const [sendingReminders, setSendingReminders] = useState(false);

  const [obForm, setObForm] = useState({ contract_id: "", title: "", description: "", responsible_party: "internal", assigned_to: "", due_date: "", priority: "medium", notes: "" });
  const [msForm, setMsForm] = useState({ contract_id: "", title: "", description: "", due_date: "" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [obRes, msRes, cRes, uRes] = await Promise.all([
        apiClient.get("/obligations"), apiClient.get("/milestones"),
        apiClient.get("/contracts"), apiClient.get("/users"),
      ]);
      setObligations(obRes.data); setMilestones(msRes.data);
      setContracts(cRes.data.items || cRes.data); setUsers(uRes.data);
    } catch { toast.error("Failed to load compliance data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const contractMap = useMemo(() => Object.fromEntries(contracts.map(c => [c.id, c.title])), [contracts]);

  const filteredObligations = useMemo(() => obligations.filter(ob => {
    if (filterContract && filterContract !== "all" && ob.contract_id !== filterContract) return false;
    if (searchTerm) return fuzzyMatch(searchTerm, [ob.title, ob.description, contractMap[ob.contract_id], ob.status, ob.compliance_status, ob.responsible_party].filter(Boolean));
    return true;
  }), [obligations, filterContract, searchTerm, contractMap]);

  const filteredMilestones = useMemo(() => milestones.filter(ms => {
    if (filterContract && filterContract !== "all" && ms.contract_id !== filterContract) return false;
    if (searchTerm) return fuzzyMatch(searchTerm, [ms.title, ms.description, contractMap[ms.contract_id], ms.status].filter(Boolean));
    return true;
  }), [milestones, filterContract, searchTerm, contractMap]);

  // Stats
  const obStats = useMemo(() => { const s = {}; filteredObligations.forEach(o => { s[o.status] = (s[o.status] || 0) + 1; }); return s; }, [filteredObligations]);
  const needsAttention = (obStats.overdue || 0) + (obStats.breached || 0);
  const complianceScore = filteredObligations.length > 0 ? Math.round(((obStats.compliant || 0) + (obStats.completed || 0)) / filteredObligations.length * 100) : 0;

  // CRUD
  const handleCreateObligation = async () => {
    if (!obForm.contract_id || !obForm.title || !obForm.due_date) { toast.error("Contract, title, and due date are required"); return; }
    try {
      const p = { ...obForm }; if (!p.assigned_to) delete p.assigned_to; if (!p.description) delete p.description; if (!p.notes) delete p.notes;
      await apiClient.post("/obligations", p); toast.success("Obligation created"); setShowObDialog(false);
      setObForm({ contract_id: "", title: "", description: "", responsible_party: "internal", assigned_to: "", due_date: "", priority: "medium", notes: "" }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleCreateMilestone = async () => {
    if (!msForm.contract_id || !msForm.title || !msForm.due_date) { toast.error("Contract, title, and due date are required"); return; }
    try {
      const p = { ...msForm }; if (!p.description) delete p.description;
      await apiClient.post("/milestones", p); toast.success("Milestone created"); setShowMsDialog(false);
      setMsForm({ contract_id: "", title: "", description: "", due_date: "" }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateObligation = async (id, updates) => { try { await apiClient.put(`/obligations/${id}`, updates); toast.success("Updated"); fetchAll(); } catch { toast.error("Failed to update"); } };
  const updateMilestone = async (id, updates) => { try { await apiClient.put(`/milestones/${id}`, updates); toast.success("Updated"); fetchAll(); } catch { toast.error("Failed to update"); } };
  const deleteObligation = async (id) => { if (!window.confirm("Delete this obligation?")) return; try { await apiClient.delete(`/obligations/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); } };
  const deleteMilestone = async (id) => { if (!window.confirm("Delete this milestone?")) return; try { await apiClient.delete(`/milestones/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); } };
  const handleSendReminders = async () => { setSendingReminders(true); try { const r = await apiClient.post("/compliance/send-reminders"); toast.success(r.data.message); } catch { toast.error("Failed"); } finally { setSendingReminders(false); } };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  const filterBannerContract = filterContract && filterContract !== "all" ? contracts.find(c => c.id === filterContract) : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Compliance Monitoring <HelpBubble tip="Define obligations and milestones. Track status updates and deadlines." />
          </h1>
          <p className="text-slate-500 mt-1">Define obligations and milestones. Status monitoring is done on the Management page.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2 rounded-sm" onClick={handleSendReminders} disabled={sendingReminders} data-testid="send-reminders-btn">
            {sendingReminders ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send Reminders
          </Button>
          <Button onClick={() => setShowObDialog(true)} className="gap-2 rounded-sm" data-testid="add-obligation-btn"><Plus size={16} /> Add Obligation</Button>
          <Button onClick={() => setShowMsDialog(true)} variant="outline" className="gap-2 rounded-sm" data-testid="add-milestone-btn"><Plus size={16} /> Add Milestone</Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="md:col-span-1 flex items-center justify-center py-4"><ComplianceScoreRing score={complianceScore} /></Card>
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: FileText, color: "bg-slate-100", iconColor: "text-slate-600", value: filteredObligations.length, label: "Obligations" },
            { icon: Flag, color: "bg-blue-50", iconColor: "text-blue-600", value: filteredMilestones.length, label: "Milestones" },
            { icon: AlertTriangle, color: "bg-red-50", iconColor: "text-red-600", value: needsAttention, label: "Needs Attention" },
            { icon: Bell, color: "bg-amber-50", iconColor: "text-amber-600", value: filteredObligations.filter(o => o.reminder_date).length + filteredMilestones.filter(m => m.reminder_date).length, label: "Reminders Set" },
          ].map(stat => (
            <Card key={stat.label}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm ${stat.color} flex items-center justify-center`}><stat.icon size={20} className={stat.iconColor} /></div>
                  <div>
                    <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <AiExtractionPanel contracts={contracts} onComplete={fetchAll} />

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-10 rounded-sm" placeholder="Search obligations & milestones..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="compliance-search" />
        </div>
        <Select value={filterContract} onValueChange={setFilterContract}>
          <SelectTrigger className="w-64 rounded-sm" data-testid="compliance-filter-contract"><SelectValue placeholder="All Contracts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contracts</SelectItem>
            {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filterBannerContract && (
        <div className="flex items-center justify-between px-4 py-3 bg-cyan-50 border border-cyan-200 rounded-sm" data-testid="contract-context-banner">
          <div>
            <p className="text-sm font-semibold text-cyan-900">{filterBannerContract.title}</p>
            <div className="flex items-center gap-3 text-[11px] text-cyan-700 mt-0.5">
              {filterBannerContract.contract_type && <span>{filterBannerContract.contract_type}</span>}
              {filterBannerContract.contracting_party?.legal_name && <span>Supplier: {filterBannerContract.contracting_party.legal_name}</span>}
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-xs text-cyan-600" onClick={() => setFilterContract("all")}>Clear filter</Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="obligations" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="obligations" className="gap-2" data-testid="obligations-tab"><ShieldCheck size={16} /> Obligations ({filteredObligations.length})</TabsTrigger>
          <TabsTrigger value="milestones" className="gap-2" data-testid="milestones-tab"><Flag size={16} /> Milestones ({filteredMilestones.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="obligations">
          <div className="space-y-2">
            {filteredObligations.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">No obligations found. Use AI extraction or add one manually.</CardContent></Card>
            ) : filteredObligations.map(ob => (
              <ObligationRow key={ob.id} ob={ob} contractMap={contractMap} users={users} onUpdate={(id, updates) => id ? updateObligation(id, updates) : fetchAll()} onDelete={deleteObligation} />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="milestones">
          <div className="space-y-2">
            {filteredMilestones.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">No milestones found. Use AI extraction or add one manually.</CardContent></Card>
            ) : filteredMilestones.map(ms => (
              <MilestoneRow key={ms.id} ms={ms} contractMap={contractMap} onUpdate={updateMilestone} onDelete={deleteMilestone} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Obligation Dialog */}
      <Dialog open={showObDialog} onOpenChange={setShowObDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Obligation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Contract *</Label>
              <Select value={obForm.contract_id} onValueChange={v => setObForm({ ...obForm, contract_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="ob-contract-select"><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent>{contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Title *</Label><Input value={obForm.title} onChange={e => setObForm({ ...obForm, title: e.target.value })} placeholder="e.g., Deliver Q1 report" className="rounded-sm" data-testid="ob-title-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={obForm.description} onChange={e => setObForm({ ...obForm, description: e.target.value })} placeholder="Details..." className="rounded-sm" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Due Date *</Label><Input type="date" value={obForm.due_date} onChange={e => setObForm({ ...obForm, due_date: e.target.value })} className="rounded-sm" data-testid="ob-due-date" /></div>
              <div className="space-y-2"><Label>Priority</Label>
                <Select value={obForm.priority} onValueChange={v => setObForm({ ...obForm, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Responsible Party</Label>
                <Select value={obForm.responsible_party} onValueChange={v => setObForm({ ...obForm, responsible_party: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="internal">Internal</SelectItem><SelectItem value="counterparty">Counterparty</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Assign To</Label>
                <Select value={obForm.assigned_to} onValueChange={v => setObForm({ ...obForm, assigned_to: v === "_none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowObDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreateObligation} className="rounded-sm" data-testid="save-obligation-btn">Create Obligation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Milestone Dialog */}
      <Dialog open={showMsDialog} onOpenChange={setShowMsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Milestone</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Contract *</Label>
              <Select value={msForm.contract_id} onValueChange={v => setMsForm({ ...msForm, contract_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="ms-contract-select"><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent>{contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Title *</Label><Input value={msForm.title} onChange={e => setMsForm({ ...msForm, title: e.target.value })} placeholder="e.g., Phase 1 delivery" className="rounded-sm" data-testid="ms-title-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={msForm.description} onChange={e => setMsForm({ ...msForm, description: e.target.value })} placeholder="Details..." className="rounded-sm" rows={2} /></div>
            <div className="space-y-2"><Label>Due Date *</Label><Input type="date" value={msForm.due_date} onChange={e => setMsForm({ ...msForm, due_date: e.target.value })} className="rounded-sm" data-testid="ms-due-date" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMsDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreateMilestone} className="rounded-sm" data-testid="save-milestone-btn">Create Milestone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
